import { fetch_form_structure, fetch_form_tree } from '@/services/main_section_service';
import {
  build_stage_timeline,
  fetch_onboarding_services,
  fetch_manual_verification_history,
  fetch_manual_verification_status,
  fetch_verification_logs,
  fetch_application_transition_history,
  fetch_configured_case_gate_ids,
  fetch_application_form,
  fetch_application_forms,
  hydrate_case_detail,
  submit_application_form_answers,
  submit_application_form_file_answers
} from '@/services/onboarding_journey_service';
import {
  build_binding_options,
  build_case_detail_checks,
  get_case_forms
} from '@/views/onboarding_system_views/Gates/Verification/manage_verification_load_helpers';
import {
  attach_case_workflow_stage,
  build_verification_statuses_by_form,
  get_bound_services,
  get_case_entity_type_id
} from '@/views/onboarding_system_views/Gates/Verification/manage_verification_helpers';
import {
  create_selected_form_state,
  load_selected_application_form_data,
  parse_application_forms,
  select_requested_application_form
} from './queue_form_helpers';
import {
  applyLoadedFormState,
  clear_system_loader_after_render,
  dispatch_queue_form_case_scope,
  empty_array,
  empty_object,
  fetch_verification_binding_data,
  find_step_number_by_section_id,
  getSelectedForm,
  show_queue_form_loader,
  clamp_step_number
} from './queue_form_method_shared';

export const queueFormLoadMethods = {
  show_queue_form_loader,
  reset_queue_form_state() {
    this.form_page_error = '';
    this.submit_success_message = '';
    this.is_submitting = false;
    this.allowed_actions = [];
    this.application_forms = [];
    this.selected_application_form_id = '';
    this.selected_application_form_meta = null;
    this.intake_steps = [];
    this.current_step = 1;
    this.workflow_stage_timeline = [];
    this.configured_gate_ids = [];
    this.workflow_transition_history = [];
    this.hydrated_case_detail = null;
    this.active_history_gate_id = '';
    this.active_history_tab = 'summary';
    this.history_panel_loading = false;
    this.history_panel_error = '';
    this.history_manual_run = null;
    this.history_verification_checks = [];
    this.expanded_history_verification_keys = [];
    this.form_values = {};
    this.file_values = {};
    this.persisted_file_urls = {};
    this.facial_verification_values = {};
    this.history_document_review_comments = {};
    this.history_document_review_in_progress_by_id = {};
    this.history_document_review_feedback = '';
    this.history_document_review_feedback_tone = 'success';
    this.reset_file_previews();
  },
  async submit_standard_answers(application_form_id, application_id = null, standard_answers = null) {
    const resolved_standard_answers = Array.isArray(standard_answers) ? standard_answers : this.buildStandardAnswers();
    const repeatable_groups = this.buildRepeatableGroups();
    if (!resolved_standard_answers.length && !repeatable_groups.length) return;
    await submit_application_form_answers({
      application_id,
      application_form_id,
      answers: resolved_standard_answers,
      repeatable_groups
    });
  },
  async submit_file_answers(application_form_id, application_id = null, standard_answers = null, file_payload = null) {
    const resolved_file_payload = file_payload || this.buildFileAnswerPayload();
    const resolved_standard_answers = Array.isArray(standard_answers) ? standard_answers : [];
    const repeatable_groups = this.buildRepeatableGroups();
    if (!resolved_file_payload.answers.length && !resolved_standard_answers.length && !repeatable_groups.length) return;
    await submit_application_form_file_answers({
      application_id,
      application_form_id,
      answers: [...resolved_standard_answers, ...resolved_file_payload.answers],
      files: resolved_file_payload.files,
      repeatable_groups
    });
  },
  async load_application_forms(options = {}) {
    const application_id = Number(this.$route.params.applicationId || 0);
    if (!application_id) {
      this.form_page_error = 'No application was supplied for this queue form.';
      return;
    }

    const target_step = Number(options?.target_step || 0) || null;
    const target_section_id = options?.target_section_id ?? null;
    const preserve_current_step = options?.preserve_current_step === true;
    const step_to_restore = target_step || (preserve_current_step ? Number(this.current_step || 1) : null);
    const load_token = Number(this.queue_form_load_token || 0) + 1;
    this.queue_form_load_token = load_token;
    this.is_loading_form_page = true;
    this.form_page_error = '';
    this.submit_success_message = '';
    show_queue_form_loader();

    try {
      const application_forms = await fetch_application_forms(application_id);
      this.application_forms = parse_application_forms(application_forms);
      const requested_application_form_id = String(this.$route.query.application_form_id || '');
      this.selected_application_form_id = select_requested_application_form(this.application_forms, requested_application_form_id);
      dispatch_queue_form_case_scope(this, getSelectedForm(this.application_forms, this.selected_application_form_id));
      if (this.selected_application_form_id) {
        await this.load_selected_application_form({ target_step: step_to_restore, target_section_id });
      }
    } catch (error) {
      this.form_page_error = error?.response?.data?.message || error?.message || 'Unable to load the assigned onboarding form.';
    } finally {
      if (this.queue_form_load_token === load_token) {
        this.is_loading_form_page = false;
        clear_system_loader_after_render(this);
      }
    }
  },
  async load_selected_application_form(options = {}) {
    this.form_page_error = '';
    this.submit_success_message = '';
    const target_step = Number(options?.target_step || 0) || null;
    const target_section_id = options?.target_section_id ?? null;
    const preserve_current_step = options?.preserve_current_step === true;
    const step_to_restore = target_step || (preserve_current_step ? Number(this.current_step || 1) : null);
    const selected_form = getSelectedForm(this.application_forms, this.selected_application_form_id);
    Object.assign(this, create_selected_form_state(selected_form));
    dispatch_queue_form_case_scope(this, selected_form);
    this.reset_file_previews();
    if (!selected_form?.form_id) return;

    await this.load_workflow_stage_timeline(selected_form);
    const loaded_form = await load_selected_application_form_data({
      application_id: Number(this.$route.params.applicationId || 0),
      entity_profile_id: Number(
        this.resolved_runtime_scope?.entity_profile_id
        || selected_form?.entity_profile_id
        || selected_form?.entity?.id
        || selected_form?.entity_profile?.id
        || 0
      ) || null,
      selected_form,
      fetch_form_tree_fn: fetch_form_tree,
      fetch_form_structure_fn: fetch_form_structure,
      fetch_application_form_fn: fetch_application_form
    }).catch((error) => {
      this.form_page_error = error?.response?.data?.message || error?.message || 'Unable to load the selected onboarding form.';
      return null;
    });
    if (!loaded_form) return;

    const [form_tree_response, saved_application_form] = loaded_form;
    if (form_tree_response?.form_load_error) {
      this.form_page_error = form_tree_response.form_load_error;
    }
    applyLoadedFormState(this, form_tree_response, saved_application_form);
    const restored_step_from_section = find_step_number_by_section_id(this.intake_steps, target_section_id);
    this.current_step = clamp_step_number(restored_step_from_section || step_to_restore || 1, this.intake_steps.length);
  },
  async load_workflow_stage_timeline(selected_form = null) {
    const application_id = Number(this.$route.params.applicationId || 0);
    const entity_profile_id = Number(
      this.resolved_runtime_scope?.entity_profile_id
      || selected_form?.entity_profile_id
      || selected_form?.entity?.id
      || selected_form?.entity_profile?.id
      || 0
    ) || null;

    if (!application_id || !entity_profile_id) {
      this.workflow_stage_timeline = [];
      this.configured_gate_ids = [];
      return;
    }

    try {
      const hydrated_case = await hydrate_case_detail({ application_id, entity_profile_id }, {
        include_documents: true,
        include_onboarding_config: false,
        include_detailed_forms: this.is_documents_gate_flow
      });
      const configured_gate_ids_promise = fetch_configured_case_gate_ids(hydrated_case).catch(() => []);
      const transition_history_promise = fetch_application_transition_history(application_id).catch(() => []);
      const history_checks_promise = this.load_history_verification_checks(hydrated_case);
      this.hydrated_case_detail = hydrated_case;
      this.workflow_stage_timeline = build_stage_timeline(hydrated_case);
      [this.configured_gate_ids, this.workflow_transition_history] = await Promise.all([
        configured_gate_ids_promise,
        transition_history_promise
      ]);
      await history_checks_promise;
      this.active_history_gate_id = '';
    } catch {
      this.hydrated_case_detail = null;
      this.workflow_stage_timeline = [];
      this.configured_gate_ids = [];
      this.workflow_transition_history = [];
      this.history_verification_checks = [];
    }
  },
  async load_history_verification_checks(case_item = {}) {
    const entity_profile_id = Number(case_item?.entity_profile_id || case_item?.profile?.id || 0);
    const entity_type_id = get_case_entity_type_id(case_item, case_item);
    const forms = get_case_forms(case_item);
    if (!entity_profile_id || !entity_type_id || !forms.length) {
      this.history_verification_checks = [];
      return;
    }

    try {
      const payload = await Promise.all([
        fetch_onboarding_services().catch(empty_array),
        fetch_verification_binding_data(entity_type_id, build_binding_options(case_item)),
        build_verification_statuses_by_form(forms).catch(empty_object),
        fetch_verification_logs(entity_profile_id).catch(empty_array),
        fetch_manual_verification_history(entity_profile_id).catch(empty_array),
        fetch_manual_verification_status(entity_profile_id).catch(empty_object)
      ]);
      const [services, verification_binding_data, verification_statuses_by_form, verification_logs, manual_history, manual_status_snapshot] = payload;
      const bound_services = get_bound_services(verification_binding_data, services);
      const case_with_stage = attach_case_workflow_stage(case_item, bound_services, verification_binding_data);
      this.history_verification_checks = build_case_detail_checks(
        case_with_stage,
        forms,
        [services, verification_binding_data, verification_statuses_by_form, verification_logs, manual_history, manual_status_snapshot]
      );
    } catch {
      this.history_verification_checks = [];
    }
  },
  async select_application_form(form_id) {
    const normalized_form_id = String(form_id || '');
    if (!normalized_form_id || normalized_form_id === String(this.selected_application_form_id || '')) return;
    this.selected_application_form_id = normalized_form_id;
    await this.load_selected_application_form();
  }
};
